import PropTypes from 'prop-types';
import React, {
  useContext, useEffect, useRef, useState,
} from 'react';
import { Col, Row } from 'antd';
import IStickyBox from 'react-sticky-box';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import _ from 'lodash';
import { UI54Card } from '@agpl/ui54kit';
import ActorInfo from '../../actors/components/infoForms/ActorInfo/ActorInfo';

import {
  getActorsInfo, getActorsInfoModal,
  getAllServices, getDefaultAdminGroupUUID, getDefaultBanGroupUUID,
} from '../../actors/selectors';

import useURLParams from '../../hooks/useURLParams';
import PublicInterfacesListModal from './PublicInterfacesListModal';
import useAuthActorsRequests from '../../actors/hooks/useAuthActorsRequests';
import { actorIsRootOrAdmin, actorsIsBanned } from '../../54origins/utils54origins';
import { getInterfacesList } from '../selectors';
import { ApiContext } from '../../api/ApiContextProvider';

function PublicInterfacesModalPage({
  modalActorUUID = '',
  hideElements = [],
  modalView = false,
}) {
  const {
    requestGetAllInterfaces,
    requestGetOpenInterfaces,
  } = useContext(ApiContext);

  const adminGroupUUID = useSelector(getDefaultAdminGroupUUID);
  const banGroupUUID = useSelector(getDefaultBanGroupUUID);

  const { requestGetActorOnAuthForModal } = useAuthActorsRequests();

  const { getURLParams, routeNavigateAndClearParams } = useURLParams();

  const {
    uuid: actorUuid,
  } = getURLParams();

  const actorData = useSelector(getActorsInfoModal);

  const allInterfaces = useSelector(getInterfacesList);

  const [isActorRootOrAdmin, setIsActorRootOrAdmin] = useState(false);

  const [isActorBanned, setIsActorBanned] = useState(false);

  const [publicInterfaces, setPublicInterfaces] = useState([]);

  const [actorFullData, setActorFullData] = useState({});

  const checkAccessRights = () => {
    setIsActorRootOrAdmin(actorIsRootOrAdmin(actorData, adminGroupUUID));
    setIsActorBanned(actorsIsBanned(actorData, banGroupUUID));
  };

  const getAllInterfaces = () => new Promise((resolve) => {
    requestGetAllInterfaces().then((response) => {
      requestGetOpenInterfaces().then();
      resolve(_.get(response, 'public_interface', []));
    });
  });

  const onSuccessDelete = () => {
    routeNavigateAndClearParams('/admin/actors/list');
  };

  const fetchPublicInterfaces = () => {
    if (isActorRootOrAdmin && !isActorBanned) {
      setPublicInterfaces(allInterfaces);
    } else if (isActorBanned) {
      setPublicInterfaces([]);
    } else {
      try {
        switch (actorFullData.actor.actor_type) {
          case 'group':
            setPublicInterfaces([...actorFullData.actor.uinfo.public_interfaces]);
            break;
          case 'user':
          case 'classic_user':
            setPublicInterfaces(actorFullData.groups.reduce((accumulator, currentObject) => {
              if (currentObject.uinfo && Array.isArray(currentObject.uinfo.public_interfaces)) {
                currentObject.uinfo.public_interfaces.forEach((item) => {
                  if (!accumulator.some((accItem) => accItem.service_name === item.service_name)) {
                    accumulator.push(item);
                  }
                });
              }
              return accumulator;
            }, []));
            break;
          default:
            setPublicInterfaces([]);
        }
      } catch {

      }
    }
  };

  const onSuccessUpdate = () => {
    requestGetActorOnAuthForModal({ uuid: actorUuid }).then((response) => { setActorFullData(response); });
  };

  useEffect(() => {
    checkAccessRights();
  }, [actorData, adminGroupUUID, banGroupUUID]);

  useEffect(() => {
    fetchPublicInterfaces();
  }, [isActorRootOrAdmin, isActorBanned, actorUuid, allInterfaces, actorFullData, actorData]);

  useEffect(() => {
    getAllInterfaces().then();
  }, []);

  useEffect(() => {
    requestGetActorOnAuthForModal({ uuid: actorUuid }).then((response) => { setActorFullData(response); });
  }, [actorUuid]);

  return (
    <Row gutter={[24, 16]}>
      <Col span={14}>
        <UI54Card variant="bordered">
          <Row gutter={[24, 16]}>
            <Col span={24}>
              <h4 className="header-primary">
                Public interfaces
              </h4>
            </Col>
            <Col span={24}>
              <PublicInterfacesListModal publicInterfaces={publicInterfaces} isActorRootOrAdmin={isActorRootOrAdmin} isActorBanned={isActorBanned} />
            </Col>
          </Row>
        </UI54Card>
      </Col>
      <Col span={10}>
        <IStickyBox offsetTop={16} offsetBottom={16}>
          <Row gutter={[24, 16]}>
            <Col span={24}>
              <ActorInfo
                onSuccessUpdate={onSuccessUpdate}
                onSuccessDelete={onSuccessDelete}
                hideElements={['permsBtn', 'applyFilterBtn', 'publicInterfacesBtn', 'editBtn', 'utilityBtn', ...hideElements]}
                modalActorUUID={modalActorUUID}
                modalView={modalView}
              />
            </Col>
          </Row>
        </IStickyBox>
      </Col>
    </Row>
  );
}

export default PublicInterfacesModalPage;

PublicInterfacesModalPage.propTypes = {
  onSuccessUpdate: PropTypes.func,
  modalActorUUID: PropTypes.string,
  modalView: PropTypes.bool,
  hideElements: PropTypes.array,
};
